import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { DateTime } from 'luxon';
import { BigScreenDashboardService } from '../../../../services/BigScreenDashboardService';
import {
  BigScreenDashboard,
  DashboardWidget,
  DashboardWidgetType,
  StatMetric,
  WidgetLayout,
  isChromelessWidget,
} from '../../../../models/big-screen-dashboard.model';
import { EntranceGroup } from '../entrance-groups/entrance-groups.component';
import { TimedEntry } from '../../../../services/attendance-chart';
import {
  entranceGroupsStorageKey,
  groupingOnStorageKey,
} from '../../../../services/attendance-metrics';
import { DashboardImportError } from '../../../../services/dashboard-transfer';

/** Which screen the full-screen overlay is currently showing. */
type BigScreenView = 'picker' | 'create' | 'view';

/**
 * Decides whether a background colour is "dark" — i.e. needs light text and the
 * dark widget palette. Uses the perceptual (sRGB-weighted) luminance so a colour
 * reads as dark when white text would be more legible on it than black. Returns
 * false for unparseable input so an odd value falls back to the light palette.
 */
function isDarkColor(hex: string): boolean {
  const match = /^#?([0-9a-f]{6}|[0-9a-f]{3})$/i.exec(hex.trim());
  if (!match) return false;
  let value = match[1];
  if (value.length === 3) {
    // Expand shorthand (#abc -> #aabbcc).
    value = value.replace(/(.)/g, '$1$1');
  }
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  // Rec. 601 luma, 0–255; below the midpoint reads as dark.
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  return luma < 140;
}

/** Default size (viewport %) for a freshly-added widget, by type. */
const DEFAULT_SIZES: Record<DashboardWidgetType, { w: number; h: number }> = {
  statTile: { w: 22.5, h: 20 },
  scanRateByEntrance: { w: 45, h: 40 },
  errorsByResultCode: { w: 45, h: 40 },
  multiLineGraph: { w: 50, h: 45 },
  entranceGraph: { w: 35, h: 35 },
  venueMap: { w: 60, h: 60 },
  image: { w: 30, h: 25 },
  textNote: { w: 30, h: 12 },
};

/**
 * Full-screen "big screen dashboard" overlay for the attendance page. When opened
 * it clears the page and lets the user pick one of their previously-saved
 * dashboards or create a new one; with none saved it drops straight into the
 * create form. Selecting a dashboard opens it as a wall display of widgets, with an
 * Edit mode for adding, configuring, dragging and resizing widgets.
 *
 * Dashboards persist to localStorage via {@link BigScreenDashboardService}; there
 * is no backend API yet. The overlay is fed the attendance page's live
 * `attendanceSummary` and timed chart data so widgets update in real time.
 */
@Component({
  selector: 'app-big-screen-dashboard',
  standalone: false,
  templateUrl: './big-screen-dashboard.component.html',
  styleUrls: ['./big-screen-dashboard.component.scss'],
})
export class BigScreenDashboardComponent implements OnChanges {
  /** Drives visibility; the attendance page flips this to open/close the overlay. */
  @Input() open: boolean = false;
  /** Mirrors the attendance page's theme so the overlay matches light/dark. */
  @Input() useDarkMode: boolean = false;

  /** Live attendance data, passed through to every widget. */
  @Input() attendanceSummary: any = {};
  @Input() venueKey: string = '';
  @Input() venueName: string = '';
  @Input() timedGateEntries: TimedEntry[] = [];
  @Input() timedScannerEntries: TimedEntry[] = [];
  @Input() chartColors: string[] = [];

  @Output() closed = new EventEmitter<void>();

  /**
   * The overlay's own theme. Seeded from {@link useDarkMode} each time it opens so
   * it matches the attendance page, then toggleable independently from the header
   * — a wall display often wants dark regardless of the operator's page theme.
   */
  darkMode: boolean = false;

  view: BigScreenView = 'picker';
  dashboards: BigScreenDashboard[] = [];
  activeDashboard: BigScreenDashboard | null = null;

  /** Whether the open dashboard is in edit mode (drag/resize/add/configure). */
  editing: boolean = false;
  /** Immersive mode: hides the header so widgets fill the whole screen. */
  fullscreen: boolean = false;
  /** Whether the floating action button's speed-dial menu is expanded. */
  fabOpen: boolean = false;
  /** The add-widget picker's visibility. */
  addWidgetOpen: boolean = false;
  /**
   * Visibility of the "switching theme clears your custom colours" warning. Shown
   * before a light/dark switch when the dashboard has custom wall or widget colours
   * that the new palette's defaults would replace.
   */
  themeWarningOpen: boolean = false;
  /** The widget currently being configured, or null. */
  configuringWidget: DashboardWidget | null = null;
  /**
   * True while the configured widget was just added and hasn't been saved yet, so
   * cancelling the config discards it rather than leaving an unchosen widget behind.
   * Also drives single-select in the config: fan-out widgets (stat tile, single-line
   * graph) only allow multi-select while new; editing an existing one is single-select.
   */
  configuringIsNew: boolean = false;

  /** This venue's saved entrance groups, read from the shared storage keys. */
  entranceGroups: EntranceGroup[] = [];

  /** New-dashboard form fields. */
  newName: string = '';
  newDescription: string = '';
  /** Set when a save is attempted with an invalid form, to show inline errors. */
  createAttempted: boolean = false;
  saveError: string = '';

  /**
   * Whether the picker is in export-selection mode: each dashboard shows a checkbox
   * so the user can pick several to export at once. Toggled by the "Export" button
   * in the picker header.
   */
  selectingForExport: boolean = false;
  /** Ids of the dashboards ticked for a bulk export. */
  private readonly exportSelection: Set<string> = new Set<string>();
  /**
   * Import feedback shown on the picker (e.g. "Imported 3 dashboards" or a mismatch
   * message). Cleared when leaving the picker or starting a new import.
   */
  importMessage: string = '';
  /** Whether {@link importMessage} is an error (styled red) rather than a success. */
  importError: boolean = false;

  constructor(private readonly dashboardService: BigScreenDashboardService) {}

  /**
   * Each time the overlay is opened, reload from storage and reset to the picker —
   * or to the create form when the user has no dashboards yet.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open'] && this.open) {
      // Adopt the page theme as the starting point each time the overlay opens.
      this.darkMode = this.useDarkMode;
      this.enterPicker();
    }
    if (changes['venueKey']) {
      this.loadEntranceGroups();
    }
  }

  /** Loads the saved dashboards and shows the picker, or the create form if empty. */
  private enterPicker(): void {
    this.dashboards = this.dashboardService.getByVenue(this.venueKey);
    this.activeDashboard = null;
    this.editing = false;
    this.fullscreen = false;
    this.fabOpen = false;
    this.addWidgetOpen = false;
    this.configuringWidget = null;
    this.themeWarningOpen = false;
    this.saveError = '';
    this.exitExportMode();
    this.clearImportMessage();
    this.view = this.dashboards.length === 0 ? 'create' : 'picker';
  }

  /**
   * Reads this venue's entrance groups from the same localStorage keys the modern
   * view uses, so grouped-entrance widgets and the modern summary stay in sync.
   */
  private loadEntranceGroups(): void {
    if (!this.venueKey) {
      this.entranceGroups = [];
      return;
    }
    try {
      const raw = localStorage.getItem(entranceGroupsStorageKey(this.venueKey));
      const parsed = raw ? JSON.parse(raw) : [];
      this.entranceGroups = Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Failed to read entrance groups for the dashboard:', error);
      this.entranceGroups = [];
    }
  }

  /** Persists edited entrance groups to the shared keys and refreshes the copy. */
  onGroupsChanged(groups: EntranceGroup[]): void {
    this.entranceGroups = groups;
    if (!this.venueKey) return;
    try {
      localStorage.setItem(entranceGroupsStorageKey(this.venueKey), JSON.stringify(groups));
      // Turn grouping on so the modern view reflects newly-created groups too.
      if (groups.length > 0) {
        localStorage.setItem(groupingOnStorageKey(this.venueKey), 'true');
      }
    } catch (error) {
      console.error('Failed to save entrance groups from the dashboard:', error);
    }
  }

  /** True when the user has no saved dashboards, used to tailor the create copy. */
  get hasNoDashboards(): boolean {
    return this.dashboards.length === 0;
  }

  showPicker(): void {
    this.dashboards = this.dashboardService.getByVenue(this.venueKey);
    this.exitExportMode();
    this.clearImportMessage();
    this.view = this.dashboards.length === 0 ? 'create' : 'picker';
  }

  showCreate(): void {
    this.newName = '';
    this.newDescription = '';
    this.createAttempted = false;
    this.saveError = '';
    this.clearImportMessage();
    this.view = 'create';
  }

  openDashboard(dashboard: BigScreenDashboard): void {
    // Work on a deep copy so drag/resize edits only reach storage via persistActive.
    this.activeDashboard = {
      ...dashboard,
      widgets: dashboard.widgets.map((w) => ({ ...w, layout: { ...w.layout } })),
    };
    this.editing = false;
    this.fullscreen = false;
    this.fabOpen = false;
    this.addWidgetOpen = false;
    this.configuringWidget = null;
    this.themeWarningOpen = false;
    // Restore the dashboard's saved palette; if it has none, follow the page theme.
    this.darkMode = dashboard.darkMode ?? this.useDarkMode;
    this.view = 'view';
  }

  /** The wall's background: the dashboard's custom colour, or '' to use the theme. */
  get backgroundColor(): string {
    return this.activeDashboard?.backgroundColor ?? '';
  }

  /** The widget-card colour: the dashboard's custom colour, or '' to use the theme. */
  get widgetColor(): string {
    return this.activeDashboard?.widgetColor ?? '';
  }

  /**
   * Whether widgets should use the dark palette: derived from the custom widget
   * colour's luminance when one is set (so text stays legible on it), otherwise
   * the dashboard's overall light/dark palette.
   */
  get widgetDark(): boolean {
    const color = this.activeDashboard?.widgetColor;
    return color ? isDarkColor(color) : this.darkMode;
  }

  /**
   * Whether the wall itself reads as dark: from the custom background colour's
   * luminance when one is set, otherwise the overall light/dark palette. Used to
   * pick text colour for chromeless widgets, which sit on the wall rather than a
   * card.
   */
  private get wallDark(): boolean {
    const color = this.activeDashboard?.backgroundColor;
    return color ? isDarkColor(color) : this.darkMode;
  }

  /**
   * The dark/light palette a specific widget should use for its text. A carded
   * widget contrasts its card ({@link widgetDark}); a chromeless one sits directly
   * on the wall, so it must contrast the wall instead — otherwise a background-off
   * tile or text note can end up with, say, light text on a light wall.
   */
  widgetIsDark(widget: DashboardWidget): boolean {
    return isChromelessWidget(widget) ? this.wallDark : this.widgetDark;
  }

  /**
   * Sets (or clears, with '') the active dashboard's custom background colour and
   * persists. Called live from the colour picker while editing. The light/dark
   * palette is left to the explicit theme toggle, so text stays where the user put
   * it regardless of the wall colour.
   */
  onBackgroundColorChange(color: string): void {
    if (!this.activeDashboard) return;
    this.activeDashboard.backgroundColor = color.trim() || undefined;
    this.persistActive();
  }

  /** Sets (or clears, with '') the custom widget-card colour and persists. */
  onWidgetColorChange(color: string): void {
    if (!this.activeDashboard) return;
    this.activeDashboard.widgetColor = color.trim() || undefined;
    this.persistActive();
  }

  /** Whether the create form's required name is present. */
  get canCreate(): boolean {
    return this.newName.trim().length > 0;
  }

  /**
   * Saves a new dashboard from the create form and opens it. A blank name is
   * rejected inline; a storage failure surfaces its message rather than losing the
   * user's input.
   */
  createDashboard(): void {
    this.createAttempted = true;
    this.saveError = '';
    const name = this.newName.trim();
    if (name.length === 0) return;
    // A dashboard is owned by the venue in context, so it needs one to be created.
    if (!this.venueKey) {
      this.saveError = 'Select a venue before creating a dashboard.';
      return;
    }

    const now = DateTime.now().toISO() ?? '';
    const dashboard: BigScreenDashboard = {
      id: this.dashboardService.createId(),
      name,
      description: this.newDescription.trim() || undefined,
      venueKey: this.venueKey,
      venueKeys: [],
      widgets: [],
      createdAt: now,
      updatedAt: now,
    };

    try {
      this.dashboardService.save(dashboard);
    } catch (error) {
      this.saveError =
        error instanceof Error ? error.message : 'Could not save the dashboard.';
      return;
    }

    this.dashboards = this.dashboardService.getByVenue(this.venueKey);
    this.openDashboard(dashboard);
  }

  /** Deletes a dashboard from the picker and refreshes the list in place. */
  deleteDashboard(dashboard: BigScreenDashboard, event: MouseEvent): void {
    event.stopPropagation();
    this.dashboardService.delete(dashboard.id);
    this.dashboards = this.dashboardService.getByVenue(this.venueKey);
    if (this.dashboards.length === 0) {
      this.view = 'create';
    }
  }

  // ---------------------------------------------------------------------------
  // Import / export (picker: bulk; dashboard view: single)
  // ---------------------------------------------------------------------------

  /** Enters export-selection mode, clearing any previous selection or message. */
  startExportMode(): void {
    this.clearImportMessage();
    this.exportSelection.clear();
    this.selectingForExport = true;
  }

  /** Leaves export-selection mode and drops the selection. */
  exitExportMode(): void {
    this.selectingForExport = false;
    this.exportSelection.clear();
  }

  isExportSelected(dashboard: BigScreenDashboard): boolean {
    return this.exportSelection.has(dashboard.id);
  }

  /** Toggles a dashboard's membership in the export selection. */
  toggleExportSelection(dashboard: BigScreenDashboard, event: MouseEvent): void {
    // In selection mode the row click must not open the dashboard.
    event.stopPropagation();
    if (this.exportSelection.has(dashboard.id)) {
      this.exportSelection.delete(dashboard.id);
    } else {
      this.exportSelection.add(dashboard.id);
    }
  }

  /** How many dashboards are ticked for export, for the button label/enablement. */
  get exportSelectionCount(): number {
    return this.exportSelection.size;
  }

  /**
   * Exports the ticked dashboards to a JSON file. Named after the single dashboard
   * when only one is picked, otherwise after the venue. Does nothing when the
   * selection is empty.
   */
  exportSelected(): void {
    const chosen = this.dashboards.filter((d) => this.exportSelection.has(d.id));
    if (chosen.length === 0) return;
    const hint = chosen.length === 1 ? chosen[0].name : this.venueName || 'venue';
    this.dashboardService.export(chosen, this.venueKey, this.venueName, hint);
    this.exitExportMode();
  }

  /** Exports the currently-open dashboard from within the dashboard view. */
  exportActive(): void {
    if (!this.activeDashboard) return;
    this.fabOpen = false;
    this.dashboardService.export(
      [this.activeDashboard],
      this.venueKey,
      this.venueName,
      this.activeDashboard.name
    );
  }

  /**
   * Handles a chosen import file for the picker's bulk import: reads it, imports the
   * dashboards as new entries on this venue, refreshes the list, and reports the
   * outcome inline. The file input is reset by the caller so the same file can be
   * re-picked.
   */
  async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.clearImportMessage();
    try {
      const raw = await file.text();
      const imported = this.dashboardService.import(raw, this.venueKey, this.venueName);
      this.dashboards = this.dashboardService.getByVenue(this.venueKey);
      this.view = 'picker';
      this.setImportMessage(
        `Imported ${imported.length} dashboard${imported.length === 1 ? '' : 's'}.`,
        false
      );
    } catch (error) {
      this.reportImportError(error);
    }
  }

  /**
   * Handles a chosen import file for the "empty dashboard" case: reads it and adopts
   * the first imported dashboard's widgets and colours into the currently-open
   * dashboard, keeping its name and id. Only offered while the open dashboard has no
   * widgets, so nothing is overwritten.
   */
  async onImportIntoActive(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.activeDashboard) return;

    this.clearImportMessage();
    this.fabOpen = false;
    try {
      const raw = await file.text();
      const [source] = this.dashboardService.readImport(raw, this.venueKey, this.venueName);
      if (!source) {
        this.setImportMessage('That file contains no dashboards to import.', true);
        return;
      }
      // Adopt the imported layout into the open dashboard, keeping its identity.
      this.activeDashboard.widgets = source.widgets;
      this.activeDashboard.backgroundColor = source.backgroundColor;
      this.activeDashboard.widgetColor = source.widgetColor;
      if (source.darkMode !== undefined) {
        this.activeDashboard.darkMode = source.darkMode;
        this.darkMode = source.darkMode;
      }
      this.persistActive();
    } catch (error) {
      this.reportImportError(error);
    }
  }

  /** Turns an import failure into an inline message, typed errors first. */
  private reportImportError(error: unknown): void {
    const message =
      error instanceof DashboardImportError
        ? error.message
        : 'Could not import that file.';
    this.setImportMessage(message, true);
  }

  private setImportMessage(message: string, isError: boolean): void {
    this.importMessage = message;
    this.importError = isError;
  }

  private clearImportMessage(): void {
    this.importMessage = '';
    this.importError = false;
  }

  // ---------------------------------------------------------------------------
  // Dashboard view — edit mode and widget CRUD
  // ---------------------------------------------------------------------------

  /** Whether the active dashboard has a custom wall or widget-card colour set. */
  get hasCustomColors(): boolean {
    return !!(this.activeDashboard?.backgroundColor || this.activeDashboard?.widgetColor);
  }

  /**
   * Handles a click on the light/dark toggle. Switching palette replaces any custom
   * wall/widget colours with the new theme's defaults, so when custom colours are
   * set we warn first and let the user back out; otherwise we switch immediately.
   */
  requestToggleDarkMode(): void {
    if (this.hasCustomColors) {
      this.themeWarningOpen = true;
      return;
    }
    this.applyDarkModeToggle();
  }

  /** Confirms the warning: clear the custom colours, then switch the palette. */
  confirmThemeSwitch(): void {
    this.themeWarningOpen = false;
    if (this.activeDashboard) {
      this.activeDashboard.backgroundColor = undefined;
      this.activeDashboard.widgetColor = undefined;
    }
    this.applyDarkModeToggle();
  }

  /** Dismisses the warning, leaving the palette and custom colours untouched. */
  cancelThemeSwitch(): void {
    this.themeWarningOpen = false;
  }

  /**
   * Flips the overlay's text/widget palette between light and dark and persists.
   * The custom-colour handling lives in {@link requestToggleDarkMode}; by the time
   * this runs any custom colours have already been cleared (or none were set).
   */
  private applyDarkModeToggle(): void {
    this.darkMode = !this.darkMode;
    if (this.activeDashboard) {
      this.activeDashboard.darkMode = this.darkMode;
    }
    this.persistActive();
  }

  /** Toggles immersive mode, which hides the header so widgets fill the screen. */
  toggleFullscreen(): void {
    this.fullscreen = !this.fullscreen;
    this.fabOpen = false;
  }

  /** Expands or collapses the floating action button's speed-dial menu. */
  toggleFab(): void {
    this.fabOpen = !this.fabOpen;
  }

  /** Collapses the speed-dial menu (e.g. after picking an action). */
  closeFab(): void {
    this.fabOpen = false;
  }

  toggleEditing(): void {
    this.editing = !this.editing;
    // Collapse the speed-dial once an action is taken; leaving edit mode also
    // tears down any open add/config surfaces.
    this.fabOpen = false;
    if (this.editing) {
      // Editing needs the header controls, so drop out of immersive mode.
      this.fullscreen = false;
    } else {
      this.addWidgetOpen = false;
      this.configuringWidget = null;
    }
  }

  openAddWidget(): void {
    // Adding a widget only makes sense in edit mode, so enter it as well.
    this.editing = true;
    this.fullscreen = false;
    this.fabOpen = false;
    this.addWidgetOpen = true;
  }

  closeAddWidget(): void {
    this.addWidgetOpen = false;
  }

  /** Creates a widget of the chosen type at the next free-ish slot and persists it. */
  addWidget(type: DashboardWidgetType): void {
    if (!this.activeDashboard) return;
    const size = DEFAULT_SIZES[type];
    const widget: DashboardWidget = {
      id: this.dashboardService.createId(),
      type,
      layout: { ...this.nextSlot(size), ...size },
      statMetric: type === 'statTile' ? 'entries' : undefined,
      useGrouping: type === 'scanRateByEntrance' ? this.entranceGroups.length > 0 : undefined,
      graphSource: type === 'multiLineGraph' || type === 'entranceGraph' ? 'gate' : undefined,
      markers: type === 'venueMap' ? [] : undefined,
      text: type === 'textNote' ? '' : undefined,
    };
    this.activeDashboard.widgets = [...this.activeDashboard.widgets, widget];
    this.addWidgetOpen = false;
    this.persistActive();
    // Configure immediately so the user sets up data points right away. Flag it as
    // new so cancelling the config discards it rather than leaving an unchosen
    // widget on the dashboard.
    this.configuringWidget = widget;
    this.configuringIsNew = true;
  }

  /**
   * Finds a starting position for a new widget by scanning the grid top-to-bottom
   * for the first cell whose default-sized rectangle doesn't overlap an existing
   * widget. Falls back to the origin when the board is full.
   */
  private nextSlot(size: { w: number; h: number }): { x: number; y: number } {
    const widgets = this.activeDashboard?.widgets ?? [];
    const step = 5;
    for (let y = 0; y + size.h <= 100; y += step) {
      for (let x = 0; x + size.w <= 100; x += step) {
        const overlaps = widgets.some((wgt) =>
          this.rectsOverlap(x, y, size.w, size.h, wgt.layout)
        );
        if (!overlaps) return { x, y };
      }
    }
    return { x: 0, y: 0 };
  }

  private rectsOverlap(x: number, y: number, w: number, h: number, other: WidgetLayout): boolean {
    return x < other.x + other.w && x + w > other.x && y < other.y + other.h && y + h > other.y;
  }

  updateWidgetLayout(widget: DashboardWidget, layout: WidgetLayout): void {
    widget.layout = layout;
    this.persistActive();
  }

  removeWidget(widget: DashboardWidget): void {
    if (!this.activeDashboard) return;
    this.activeDashboard.widgets = this.activeDashboard.widgets.filter((w) => w.id !== widget.id);
    this.persistActive();
  }

  configureWidget(widget: DashboardWidget): void {
    // Reconfiguring an existing widget: cancelling should keep it as-is.
    this.configuringWidget = widget;
    this.configuringIsNew = false;
  }

  closeConfigure(): void {
    // A just-added widget that was never saved is discarded on cancel, so the
    // dashboard isn't left with a widget the user didn't choose to keep.
    if (this.configuringIsNew && this.configuringWidget) {
      this.removeWidget(this.configuringWidget);
    }
    this.configuringWidget = null;
    this.configuringIsNew = false;
  }

  /** Replaces the configured widget with the edited copy and persists. */
  onWidgetConfigured(updated: DashboardWidget): void {
    if (!this.activeDashboard) return;

    // A single-entrance graph shows one line, so selecting several entrances
    // fans out into one widget per line: the configured widget keeps the first,
    // and each remaining entrance spawns its own graph tiled into a free slot.
    const names = updated.entranceNames;
    // A stat tile shows one metric, so selecting several fans out the same way:
    // the configured tile takes the first metric, each other metric a new tile.
    const metrics = updated.statMetrics;
    if (updated.type === 'statTile' && metrics && metrics.length > 0) {
      updated.statMetric = metrics[0];
      updated.statMetrics = undefined; // transient carrier, never persisted
      this.activeDashboard.widgets = this.activeDashboard.widgets.map((w) =>
        w.id === updated.id ? updated : w
      );
      for (const metric of metrics.slice(1)) {
        this.activeDashboard.widgets = [
          ...this.activeDashboard.widgets,
          this.cloneStatTile(updated, metric),
        ];
      }
    } else if (updated.type === 'entranceGraph' && names && names.length > 1) {
      updated.entranceNames = [names[0]];
      this.activeDashboard.widgets = this.activeDashboard.widgets.map((w) =>
        w.id === updated.id ? updated : w
      );
      for (const name of names.slice(1)) {
        this.activeDashboard.widgets = [
          ...this.activeDashboard.widgets,
          this.cloneEntranceGraph(updated, name),
        ];
      }
    } else {
      this.activeDashboard.widgets = this.activeDashboard.widgets.map((w) =>
        w.id === updated.id ? updated : w
      );
    }

    this.configuringWidget = null;
    // The widget is now saved, so a later cancel must not discard it.
    this.configuringIsNew = false;
    this.persistActive();
  }

  /**
   * Builds a fresh single-line graph widget for one entrance, copying the source
   * widget's source/title but giving it a new id and its own free grid slot.
   */
  private cloneEntranceGraph(source: DashboardWidget, entranceName: string): DashboardWidget {
    const size = { w: source.layout.w, h: source.layout.h };
    return {
      id: this.dashboardService.createId(),
      type: 'entranceGraph',
      title: source.title,
      titleFontSize: source.titleFontSize,
      titleBold: source.titleBold,
      titleColor: source.titleColor,
      layout: { ...this.nextSlot(size), ...size },
      graphSource: source.graphSource,
      entranceNames: [entranceName],
    };
  }

  /**
   * Builds a fresh stat tile for one metric, copying the source tile's size but
   * giving it a new id and its own free grid slot. No title is copied so each tile
   * shows its own metric's default label, but the title *styling* is carried so the
   * fanned-out tiles read as a consistent set.
   */
  private cloneStatTile(source: DashboardWidget, metric: StatMetric): DashboardWidget {
    const size = { w: source.layout.w, h: source.layout.h };
    return {
      id: this.dashboardService.createId(),
      type: 'statTile',
      layout: { ...this.nextSlot(size), ...size },
      statMetric: metric,
      // Fanned-out tiles share the source tile's background and title styling.
      hideBackground: source.hideBackground,
      titleFontSize: source.titleFontSize,
      titleBold: source.titleBold,
      titleColor: source.titleColor,
    };
  }

  /**
   * Applies an in-place widget update from the widget body (e.g. a venue-map
   * marker dragged to a new spot) without touching the config modal, then
   * persists. Unlike {@link onWidgetConfigured} this fires during normal editing.
   */
  onWidgetContentChanged(updated: DashboardWidget): void {
    if (!this.activeDashboard) return;
    this.activeDashboard.widgets = this.activeDashboard.widgets.map((w) =>
      w.id === updated.id ? updated : w
    );
    this.persistActive();
  }

  trackByWidget(_index: number, widget: DashboardWidget): string {
    return widget.id;
  }

  /** Saves the active dashboard, stamping its updated time. */
  private persistActive(): void {
    if (!this.activeDashboard) return;
    this.activeDashboard.updatedAt = DateTime.now().toISO() ?? this.activeDashboard.updatedAt;
    try {
      this.dashboardService.save(this.activeDashboard);
    } catch (error) {
      console.error('Failed to persist the dashboard:', error);
    }
  }

  close(): void {
    this.closed.emit();
  }

  trackById(_index: number, dashboard: BigScreenDashboard): string {
    return dashboard.id;
  }
}
